import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Bell, MessageCircle, Users, TrendingUp, Trophy, UserPlus, Megaphone, Loader2 } from 'lucide-react';
import { Separator } from '@/components/ui/separator';

interface NotificationPrefs {
  buddy_read_invites: boolean;
  buddy_read_progress: boolean;
  messages: boolean;
  friend_level_ups: boolean;
  friend_achievements: boolean;
  follow_requests: boolean;
  system_announcements: boolean;
}

const notificationSettings = [
  {
    key: 'buddy_read_invites' as keyof NotificationPrefs,
    title: 'Buddy Read Invitations',
    description: 'Get notified when friends invite you to buddy reads',
    icon: Users,
  },
  {
    key: 'buddy_read_progress' as keyof NotificationPrefs,
    title: 'Buddy Read Progress',
    description: 'See when friends update their progress in buddy reads',
    icon: Users,
  },
  {
    key: 'messages' as keyof NotificationPrefs,
    title: 'Messages',
    description: 'Receive notifications for new messages',
    icon: MessageCircle,
  },
  {
    key: 'friend_level_ups' as keyof NotificationPrefs,
    title: 'Friend Level Ups',
    description: 'Celebrate when your friends reach new levels',
    icon: TrendingUp,
  },
  {
    key: 'friend_achievements' as keyof NotificationPrefs,
    title: 'Friend Achievements',
    description: 'See when friends unlock new achievements',
    icon: Trophy,
  },
  {
    key: 'follow_requests' as keyof NotificationPrefs,
    title: 'Follow Requests',
    description: 'Get notified of new follow requests',
    icon: UserPlus,
  },
  {
    key: 'system_announcements' as keyof NotificationPrefs,
    title: 'System Announcements',
    description: 'Important updates and announcements from Dino',
    icon: Megaphone,
  },
];

export function NotificationPreferences() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [preferences, setPreferences] = useState<NotificationPrefs>({
    buddy_read_invites: true,
    buddy_read_progress: true,
    messages: true,
    friend_level_ups: true,
    friend_achievements: true,
    follow_requests: true,
    system_announcements: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadPreferences();
  }, [user]);

  const loadPreferences = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        setPreferences({
          buddy_read_invites: data.buddy_read_invites,
          buddy_read_progress: data.buddy_read_progress,
          messages: data.messages,
          friend_level_ups: data.friend_level_ups,
          friend_achievements: data.friend_achievements,
          follow_requests: data.follow_requests,
          system_announcements: data.system_announcements,
        });
      }
    } catch (error) {
      console.error('Error loading notification preferences:', error);
      toast({
        title: 'Error',
        description: 'Failed to load notification preferences',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key: keyof NotificationPrefs) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSave = async () => {
    if (!user) return;

    try {
      setSaving(true);
      const { error } = await supabase
        .from('notification_preferences')
        .upsert({
          user_id: user.id,
          ...preferences,
        });

      if (error) throw error;

      toast({
        title: 'Preferences saved',
        description: 'Your notification preferences have been updated',
      });
    } catch (error) {
      console.error('Error saving notification preferences:', error);
      toast({
        title: 'Error',
        description: 'Failed to save notification preferences',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          <CardTitle>Notification Preferences</CardTitle>
        </div>
        <CardDescription>
          Choose which notifications you want to receive
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          {notificationSettings.map((setting, index) => {
            const Icon = setting.icon;
            return (
              <div key={setting.key}>
                <div className="flex items-center justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <Icon className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div className="space-y-0.5">
                      <Label
                        htmlFor={setting.key}
                        className="text-base font-medium cursor-pointer"
                      >
                        {setting.title}
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        {setting.description}
                      </p>
                    </div>
                  </div>
                  <Switch
                    id={setting.key}
                    checked={preferences[setting.key]}
                    onCheckedChange={() => handleToggle(setting.key)}
                  />
                </div>
                {index < notificationSettings.length - 1 && (
                  <Separator className="mt-4" />
                )}
              </div>
            );
          })}
        </div>

        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full"
        >
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Preferences'
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
